'use client';

import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, CheckboxIndicator } from '@radix-ui/react-checkbox';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { ProjectResponseAudio } from '@/shared-survey';
import { deduplicateOptions } from '@/shared-utilities';

import { ProjectResponseAssetItem } from './project-response-asset-item';
import { useImageChoice } from './project-response-image-choice.hooks';
import ProjectResponseMessageRendererAudio from './project-response-message-renderer-audio';
import { ProjectResponseQuestionText } from './project-response-question-text';
import { ProjectResponseComponentProps } from './project-response-renderer.type';

interface ProjectResponseImageChoiceProps {
  app?: 'manager' | 'survey';
  options: NonNullable<ProjectResponseComponentProps['options']>;
  value: string;
  tentativeValue?: string;
  onClick: (value: string) => void;
  readonly?: boolean;
  maxSelections?: number;
  assets?: ProjectResponseComponentProps['assets'];
  text?: string;
  audio?: ProjectResponseAudio;
  isAiVoiceEnabled?: boolean;
}

export default function ProjectResponseImageChoice({
  app,
  options,
  value,
  tentativeValue,
  onClick,
  readonly,
  maxSelections,
  text,
  assets,
  audio,
  isAiVoiceEnabled,
}: ProjectResponseImageChoiceProps) {
  const t = useTranslations('Usability');

  const uniqueOptions = deduplicateOptions(options);

  // Use hook for selection logic
  const { isValueSelected, handleClick } = useImageChoice(
    value,
    onClick,
    maxSelections,
    readonly,
    tentativeValue
  );

  return (
    <div
      data-test='project-response-image-choice'
      className={cn(
        'flex min-w-full flex-col justify-center gap-4 rounded-2xl border border-b-2 border-gray-200 bg-(--brand-assistant-message-bg) px-6 py-8',
        app &&
          'border border-(--brand-separation-border) shadow-(--brand-separation-shadow) backdrop-blur-sm'
      )}
    >
      {isAiVoiceEnabled && (
        <ProjectResponseMessageRendererAudio audio={audio ?? undefined} />
      )}

      {text && (
        <ProjectResponseQuestionText
          text={text}
          className={cn(app && 'text-(--brand-text-on-assistant-message)')}
        />
      )}

      {assets && assets?.length > 0 && assets[0]?.url && (
        <ProjectResponseAssetItem asset={assets[0]} />
      )}

      <div className='flex w-full flex-wrap items-start justify-start gap-4'>
        {uniqueOptions.map((option) => {
          const selected = isValueSelected(option.value);
          const imageUrl = option.assets?.[0]?.url;

          return (
            /* oxlint-disable jsx-a11y/prefer-tag-over-role */
            <div
              key={option.value}
              role='button'
              tabIndex={readonly ? -1 : 0}
              aria-disabled={readonly}
              aria-pressed={selected}
              onClick={() => !readonly && handleClick(option.value)}
              onKeyDown={(e) => {
                if (readonly) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick(option.value);
                }
              }}
              className={cn(
                'flex-col items-start gap-2 overflow-hidden rounded-2xl border pb-2 transition-all',
                selected
                  ? 'border-sky-500 ring-2 ring-sky-500'
                  : 'border-gray-200 hover:border-gray-300',
                readonly ? 'cursor-not-allowed' : 'cursor-pointer',
                app
                  ? 'w-34 border-(--brand-separation-border) bg-white shadow-(--brand-separation-shadow)'
                  : 'w-60 bg-linear-to-r from-rose-50 to-indigo-50',
                app && selected && 'border-(--brand-primary) shadow-xl ring-0'
              )}
              data-test='image-choice-option'
            >
              {imageUrl && (
                <div
                  data-context={app}
                  className='h-48 w-full p-2 data-context:h-24'
                >
                  <img
                    src={imageUrl}
                    alt={option.label}
                    className='size-full rounded-sm object-contain'
                    data-test='image-choice-image'
                  />
                </div>
              )}
              <div className='w-full text-center [font-family:var(--brand-font)] text-base text-gray-900'>
                {option.label}
              </div>
              {/* oxlint-enable jsx-a11y/prefer-tag-over-role */}
              {app && (
                <div className='mt-3 mb-2 flex w-full items-center justify-center'>
                  <Checkbox
                    checked={selected}
                    disabled={readonly}
                    className='flex size-4 items-center justify-center rounded border border-gray-300 bg-white text-(--brand-primary) transition-colors focus:ring-2 focus:ring-(--brand-primary) focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-(--brand-primary) data-[state=checked]:bg-(--brand-primary)'
                    data-test='image-choice-checkbox'
                    aria-label={`${option.label} ${selected ? t('optionSelectedAria') : t('optionNotSelectedAria')}`}
                  >
                    <CheckboxIndicator>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className='size-3 text-white'
                        aria-hidden='true'
                      />
                    </CheckboxIndicator>
                  </Checkbox>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
